import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Swords, Plus, Loader2, AlertCircle, HelpCircle, History, Gem, ArrowUp, ArrowDown } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import useBattleStore from '../store/useBattleStore'
import useAppStore from '../../../store/useAppStore'
import BattleCard from '../components/BattleCard'
import useAudioManager from '../../../hooks/useAudioManager'
import HelpModal from '../../../components/HelpModal'
import { hapticImpact } from '../../../utils/haptic'

const BATTLE_PRICE_SORT_STORAGE_KEY = 'battle-price-sort-order'

const Battles = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { battles, isLoading, error, loadBattles } = useBattleStore()
  const { loadProfile, user } = useAppStore()
  const [refreshing, setRefreshing] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [priceSortOrder, setPriceSortOrder] = useState(() => {
    if (typeof window === 'undefined') {
      return 'asc'
    }

    const storedSortOrder = window.localStorage.getItem(BATTLE_PRICE_SORT_STORAGE_KEY)
    return storedSortOrder === 'desc' ? 'desc' : 'asc'
  })
  const { playSound } = useAudioManager({
    select: 'select'
  })
  const [showHelp, setShowHelp] = useState(false)

  const seenBattleIdsRef = useRef(new Set())
  const isInitialSeenSetRef = useRef(false)
  const [animatedBattleIds, setAnimatedBattleIds] = useState(new Set())

  // Обновляем профиль (баланс) при монтировании страницы
  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  useEffect(() => {
    // Первая загрузка
    loadBattles().finally(() => {
      setIsInitialLoading(false)
    })
    
    // Polling каждые 3 секунды для обновления списка баттлов
    // (до появления WebSocket)
    const interval = setInterval(() => {
      loadBattles()
    }, 3000)
    
    return () => clearInterval(interval)
  }, [loadBattles])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(BATTLE_PRICE_SORT_STORAGE_KEY, priceSortOrder)
  }, [priceSortOrder])

  useEffect(() => {
    if (isInitialLoading) return

    const ids = (battles || [])
      .map(b => (b?.id != null ? String(b.id) : null))
      .filter(Boolean)

    // При первом показе страницы просто помечаем все текущие баттлы как уже виденные
    if (!isInitialSeenSetRef.current) {
      ids.forEach(id => seenBattleIdsRef.current.add(id))
      isInitialSeenSetRef.current = true
      return
    }

    const newIds = ids.filter(id => !seenBattleIdsRef.current.has(id))
    if (newIds.length === 0) return

    // Помечаем новые баттлы для одноразовой анимации
    setAnimatedBattleIds(prev => {
      const next = new Set(prev)
      newIds.forEach(id => next.add(id))
      return next
    })

    newIds.forEach(id => seenBattleIdsRef.current.add(id))

    const timeoutId = setTimeout(() => {
      setAnimatedBattleIds(prev => {
        const next = new Set(prev)
        newIds.forEach(id => next.delete(id))
        return next
      })
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [battles, isInitialLoading])

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadBattles()
    setRefreshing(false)
  }

  const handleCreateBattle = () => {
    playSound('select')
    hapticImpact('medium')
    navigate('/battles/create')
  }

  const sortedBattles = useMemo(() => {
    const userBalance = Number(user?.balance) || 0

    const getBattlePrice = (battle) => {
      const needBalance = Number(battle?.need_balance)
      if (Number.isFinite(needBalance) && needBalance > 0) {
        return needBalance
      }

      const casePrice = Number(battle?.case?.price) || 0
      const countCase = Number(battle?.count_case) || 1
      return casePrice * countCase
    }

    const getAvailableSlots = (battle) => {
      const currentPlayers = battle?.players?.length || 0
      const totalSlots = parseInt(battle?.count_players, 10) || 0
      return Math.max(totalSlots - currentPlayers, 0)
    }

    return [...battles].sort((a, b) => {
      // 1) Доступные по бюджету баттлы показываем выше
      const aPrice = getBattlePrice(a)
      const bPrice = getBattlePrice(b)
      const aAffordable = aPrice <= userBalance
      const bAffordable = bPrice <= userBalance

      if (aAffordable !== bAffordable) {
        return aAffordable ? -1 : 1
      }

      // 2) Ручная сортировка по цене (дешевые/дорогие)
      const priceDiff = priceSortOrder === 'asc' ? aPrice - bPrice : bPrice - aPrice
      if (priceDiff !== 0) {
        return priceDiff
      }

      // 3) Внутри одинаковой цены выше те, где меньше свободных слотов
      return getAvailableSlots(a) - getAvailableSlots(b)
    })
  }, [battles, priceSortOrder, user?.balance])

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Background Image - показываем только если баттлов нет */}
      {battles.length === 0 && (
        <>
          <div 
            className="fixed inset-0 bg-cover bg-top bg-no-repeat"
            style={{ backgroundImage: 'url(/images/case-battle-bg.png)' }}
          />
          
          {/* Dark Overlay */}
          <div className="fixed inset-0 bg-dark-primary/50" />
        </>
      )}
      
      {/* Content */}
      <div className="relative container mx-auto px-4 py-6 pb-12 max-w-2xl lg:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 lg:mb-12">
        <div className="flex items-center gap-3">
          <Swords className="w-6 h-6 text-accent-primary lg:w-8 lg:h-8" />
          <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl">
            {t('battle.title', 'Кейс-Баттлы')}
          </h1>
          <button
            onClick={() => setShowHelp(true)}
            className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
          >
            <HelpCircle className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              playSound('select')
              hapticImpact('light')
              setPriceSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            }}
            className="group relative flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 rounded-xl border border-accent-primary/25 bg-gradient-to-b from-dark-secondary/90 to-dark-secondary/60 hover:border-accent-primary/50 hover:shadow-[0_0_20px_rgba(0,255,127,0.22)] transition-all duration-200"
          >
            {priceSortOrder === 'asc' ? (
              <ArrowUp className="w-5 h-5 lg:w-6 lg:h-6 text-accent-primary/90 group-hover:text-accent-primary transition-colors" />
            ) : (
              <ArrowDown className="w-5 h-5 lg:w-6 lg:h-6 text-accent-primary/90 group-hover:text-accent-primary transition-colors" />
            )}
          </button>

          <button
            onClick={handleCreateBattle}
            className="flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-xl font-semibold hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Battles List */}
      {battles.length === 0 ? (
        <div className="text-center pt-12">
          <Swords className="w-16 h-16 text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary text-lg mb-4">
            {t('battle.emptyState.title')}
          </p>
          <button
            onClick={handleCreateBattle}
            className="px-6 py-3 bg-accent-primary text-dark-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            {t('battle.createFirst')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {sortedBattles.map((battle) => (
                <BattleCard
                  key={battle.id}
                  battle={battle}
                  shouldAnimate={animatedBattleIds.has(String(battle.id))}
                />
              ))}
          </AnimatePresence>
        </div>
      )}

      {/* Ссылка на историю баттлов */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={() => {
            playSound('select')
            hapticImpact('light')
            navigate('/battles/history')
          }}
          className="flex items-center gap-2 text-text-secondary transition-colors"
        >
          <History className="w-4 h-4" />
          <span className="text-sm">{t('battle.viewHistory', 'История баттлов')}</span>
        </button>
      </div>

      {/* Refresh Indicator */}
      {refreshing && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 bg-surface px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-accent-primary" />
          <span className="text-sm text-text-secondary">
            {t('battle.refreshing', 'Обновление...')}
          </span>
        </div>
      )}
      </div>

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('battle.help.title')}
        steps={[
          {
            title: t('battle.help.step1Title'),
            description: t('battle.help.step1Desc')
          },
          {
            title: t('battle.help.step2Title'),
            description: t('battle.help.step2Desc')
          },
          {
            title: t('battle.help.step3Title'),
            description: t('battle.help.step3Desc')
          },
          {
            title: t('battle.help.step4Title'),
            description: t('battle.help.step4Desc')
          }
        ]}
      />
    </div>
  )
}

export default Battles
